import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { unauthAxiosFetcher } from "../axios";
import {
  rewriteSummerReasons,
  worldWonderReasons,
  mongoliaDestinationReasons,
  japowDestinationReasons,
  weekendDestinationReasons,
  japowWhyUsReasons,
  japowReviews,
  weekendReviews,
  japowFaqs,
  weekendPassFaqs,
  leaderDestinationReasons,
  leaderReviews,
  leaderFaqs,
  europeDestinationReasons,
  europeFaqs,
} from "../constants/destination";
import { customCheckout } from "../services/stripe";
import { FAQs, Footer, HeroFeatured, StickyHeader, WhyChooseUs } from "./components/landing-new";
import useQueryParams from "../customHooks/useQueryParams";

import { DisplayRow } from "../home/DisplayRow.js";
import {
  landingVideoDesktop,
  landingVideoMobile,
  landingImageDesktop,
  landingImageMobile,
} from "../utils/constants";

// Leader Invitation Urgency Banner Component
const LeaderInvitationBanner = ({ invitationKey }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expiryDate, setExpiryDate] = useState(null);

  useEffect(() => {
    const fetchInvitationDetails = async () => {
      if (!invitationKey) {
        setIsLoading(false);
        setIsExpired(true);
        return;
      }

      try {
        const response = await unauthAxiosFetcher(`/v1/leader-invitation/${invitationKey}`);

        if (response.success && response.data) {
          const { expired_at, is_expired } = response.data;

          if (is_expired) {
            setIsExpired(true);
            setIsLoading(false);
            return;
          }

          setExpiryDate(new Date(expired_at));
          setIsLoading(false);
        } else {
          setIsExpired(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching invitation details:", error);
        setIsExpired(true);
        setIsLoading(false);
      }
    };

    fetchInvitationDetails();
  }, [invitationKey]);

  useEffect(() => {
    if (!expiryDate || isExpired) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const distance = expiryDate.getTime() - now;

      if (distance < 0) {
        setIsExpired(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [expiryDate, isExpired]);

  const handleJoinCommunity = () => {
    // Open Telegram link to contact/join leader groups
    window.open("https://t.me/+MUhSD9ONCP05MDE1", "_blank");
  };

  // Don't show banner if loading, expired, or no invitation key
  if (isLoading || isExpired || !invitationKey) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
      }}
      className="tw-fixed tw-bottom-4 tw-left-4 tw-right-4 tw-z-50 md:tw-bottom-6 md:tw-left-6 md:tw-right-6"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="tw-mx-auto tw-max-w-7xl tw-rounded-2xl tw-border tw-border-white/20 tw-bg-black/40 tw-p-4 tw-shadow-2xl tw-backdrop-blur-md md:tw-rounded-3xl md:tw-p-6"
      >
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 md:tw-flex-row md:tw-justify-between md:tw-gap-6">
          <div className="tw-text-center tw-text-white md:tw-text-left">
            <h3 className="tw-mb-2 tw-text-base tw-font-bold md:tw-mb-3 md:tw-text-xl">
              {t("landing.destination.leader_invitation.expiring_in")}
            </h3>
            <div className="tw-flex tw-justify-center tw-gap-2 tw-text-sm tw-font-semibold md:tw-justify-start md:tw-gap-3 md:tw-text-base">
              <div className="tw-flex tw-flex-col tw-items-center">
                <span className="tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1.5 tw-text-xl tw-font-bold tw-text-primary-main tw-backdrop-blur-sm md:tw-px-4 md:tw-py-2 md:tw-text-2xl">
                  {timeLeft.days}
                </span>
                <span className="tw-mt-1 tw-text-xs tw-uppercase tw-text-white/90">
                  {t("landing.destination.leader_invitation.days")}
                </span>
              </div>
              <div className="tw-flex tw-flex-col tw-items-center">
                <span className="tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1.5 tw-text-xl tw-font-bold tw-text-primary-main tw-backdrop-blur-sm md:tw-px-4 md:tw-py-2 md:tw-text-2xl">
                  {timeLeft.hours}
                </span>
                <span className="tw-mt-1 tw-text-xs tw-uppercase tw-text-white/90">
                  {t("landing.destination.leader_invitation.hours")}
                </span>
              </div>
              <div className="tw-flex tw-flex-col tw-items-center">
                <span className="tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1.5 tw-text-xl tw-font-bold tw-text-primary-main tw-backdrop-blur-sm md:tw-px-4 md:tw-py-2 md:tw-text-2xl">
                  {timeLeft.minutes}
                </span>
                <span className="tw-mt-1 tw-text-xs tw-uppercase tw-text-white/90">
                  {t("landing.destination.leader_invitation.minutes")}
                </span>
              </div>
              <div className="tw-flex tw-flex-col tw-items-center">
                <span className="tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1.5 tw-text-xl tw-font-bold tw-text-primary-main tw-backdrop-blur-sm md:tw-px-4 md:tw-py-2 md:tw-text-2xl">
                  {timeLeft.seconds}
                </span>
                <span className="tw-mt-1 tw-text-xs tw-uppercase tw-text-white/90">
                  {t("landing.destination.leader_invitation.seconds")}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleJoinCommunity}
            className="hover:tw-bg-primary-dark tw-w-full tw-whitespace-nowrap tw-rounded-full tw-bg-primary-main tw-px-6 tw-py-3 tw-text-sm tw-font-bold tw-text-white tw-transition-all hover:tw-scale-105 hover:tw-shadow-xl md:tw-w-auto md:tw-px-8 md:tw-py-3.5 md:tw-text-base"
          >
            {t("landing.destination.leader_invitation.telegram_group_button")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Destination = ({ destinationKey = "mongolia" }) => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const passPurchased = params.get("pass-purchased");
  const invitationKey = params.get("invitation_key");

  useEffect(() => {
    if (passPurchased === "true") {
      alert(t("landing.destination.weekend_pass_purchase_success"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passPurchased]);

  // Configuration object for different destinations
  const destinationConfig = {
    "rewrite-summer": {
      country: "Rewrite your summer",
      broad_category: "rewrite_summer",
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/1777968472116_summerpromo_1920x1080.mp4",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/1777968480638_summerpromo_1080x1920.mp4",
      },
      cta: {
        text: t("landing.destination.rewrite-summer.cta_text"),
        scrollTarget: "destination-tours",
      },
      mainText: t("landing.destination.rewrite-summer.landing_title"),
      subText: t("landing.destination.rewrite-summer.landing_desc"),
      title: t("landing.destination.rewrite-summer.title"),
      whyDestinationTitle: t("landing.destination.rewrite-summer.why_destination_title"),
      whyDestinationDescription: t(
        "landing.destination.rewrite-summer.why_destination_description"
      ),
      whyDestinationMobileScrollVariant: "vertical",
      destinationReasons: rewriteSummerReasons,
    },
    "world-wonders": {
      country: "World wonders",
      broad_category: "world_wonders",
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/world_desktop.mov",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/world_mobile.mov",
      },
      cta: {
        text: t("landing.destination.world-wonders.cta_text"),
        scrollTarget: "destination-tours",
      },
      mainText: t("landing.destination.world-wonders.landing_title"),
      subText: t("landing.destination.world-wonders.landing_desc"),
      title: t("landing.destination.world-wonders.title"),
      whyDestinationTitle: t("landing.destination.world-wonders.why_destination_title"),
      whyDestinationDescription: t("landing.destination.world-wonders.why_destination_description"),
      whyDestinationMobileScrollVariant: "vertical",
      destinationReasons: worldWonderReasons,
    },
    mongolia: {
      country: "Mongolia",
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/mongolia_landing_vid_desktop.mov",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/mongolia_landing_vid_mobile.mov",
      },
      cta: {
        text: t("landing.destination.mongolia.cta_text"),
        scrollTarget: "destination-tours",
      },
      title: t("landing.destination.mongolia.title"),
      whyDestinationTitle: t("landing.destination.mongolia.why_destination_title"),
      whyDestinationDescription: t("landing.destination.mongolia.why_destination_description"),
      destinationReasons: mongoliaDestinationReasons,
    },
    japow: {
      country: "Japan",
      broad_category: "snow",
      image: {
        desktop:
          "https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/147846020260602-210852.png",
        mobile:
          "https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/147846020260602-210852.png",
      },
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/japow-25-desktop.mp4",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/japow-25-mobile.mp4",
      },
      cta: {
        text: t("landing.destination.japow.cta_text"),
        scrollTarget: "destination-tours",
      },
      title: t("landing.destination.japow.title"),
      // Built for progression (whyUs) → reviews → why-visit (whyDestination)
      sectionOrder: ["whyUs", "reviews", "whyDestination"],
      whyDestinationTitle: t("landing.destination.japow.why_destination_title"),
      whyDestinationDescription: t("landing.destination.japow.why_destination_description"),
      destinationReasons: japowDestinationReasons,

      whyUsTitle: t("landing.destination.japow.why_us_title"),
      whyUsDescription: t("landing.destination.japow.why_us_description"),
      whyUsReasons: japowWhyUsReasons,

      reviewsTitle: t("landing.destination.japow.reviews_title"),
      reviewsDescription: t("landing.destination.japow.reviews_description"),
      reviewsReasons: japowReviews,
      customFaqs: japowFaqs,
    },
    weekend: {
      country: "Weekend",
      broad_category: "weekend_pass",
      fallBackImage: "https://d1puf2nnbcyl9q.cloudfront.net/weekend_landing.jpg",
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/weekend_landing.mp4",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/weekend_landing_mobile.mov",
      },
      cta: {
        text: t("landing.destination.weekend.cta_text"),
        scrollTarget: "destination-tours",
      },
      title: t("landing.destination.weekend.title"),
      whyDestinationTitle: t("landing.destination.weekend.why_destination_title"),
      whyDestinationDescription: t("landing.destination.weekend.why_destination_description"),
      destinationReasons: weekendDestinationReasons,

      // whyUsTitle: "Save big with the weekend pass",
      // whyUsDescription: "Your one pass to rule them all",
      // whyUsReasons: weekendPassExplained,
      // whyUsMobileScrollVariant: "horizontal",

      reviewsTitle: t("landing.destination.weekend.reviews_title"),
      reviewsDescription: t("landing.destination.weekend.reviews_description"),
      reviewsReasons: weekendReviews,
      reviewMobileScrollVariant: "vertical",

      customFaqs: weekendPassFaqs,
    },
    "beginner-leader-trips": {
      country: "Leader trips",
      broad_category: "beginner_leader_trips",
      fallBackImage: landingImageDesktop,
      videos: {
        desktop: landingVideoDesktop,
        mobile: landingVideoMobile,
      },
      mainText: t("landing.destination.beginner_leader_trips.main_text"),
      subText: t("landing.destination.beginner_leader_trips.sub_text"),
      cta: {
        text: t("landing.destination.beginner_leader_trips.cta_text"),
        scrollTarget: "destination-tours",
      },

      title: t("landing.destination.beginner_leader_trips.title"),

      whyDestinationTitle: t("landing.destination.beginner_leader_trips.why_destination_title"),
      whyDestinationDescription: t(
        "landing.destination.beginner_leader_trips.why_destination_description"
      ),
      destinationReasons: leaderDestinationReasons,

      reviewsTitle: t("landing.destination.beginner_leader_trips.reviews_title"),
      reviewsDescription: t("landing.destination.beginner_leader_trips.reviews_description"),
      reviewsReasons: leaderReviews,
      reviewMobileScrollVariant: "horizontal",

      customFaqs: leaderFaqs,
    },
    "powderpuff-sale": {
      country: "Europe, Japan, China",
      broad_category: "powderpuff_sale",
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/powderpuff.mp4",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/powderpuff.mp4",
      },
      cta: {
        text: t("landing.destination.powderpuff_sale.cta_text"),
        scrollTarget: "destination-tours",
      },
      title: t("landing.destination.powderpuff_sale.title"),
      whyDestinationTitle: t("landing.destination.powderpuff_sale.why_destination_title"),
      whyDestinationDescription: t(
        "landing.destination.powderpuff_sale.why_destination_description"
      ),
      destinationReasons: japowDestinationReasons,

      whyUsTitle: t("landing.destination.powderpuff_sale.why_us_title"),
      whyUsDescription: t("landing.destination.powderpuff_sale.why_us_description"),
      whyUsReasons: japowWhyUsReasons,
      mobileVariant: "vertical",

      reviewsTitle: t("landing.destination.powderpuff_sale.reviews_title"),
      reviewsDescription: t("landing.destination.powderpuff_sale.reviews_description"),
      reviewsReasons: japowReviews,
      customFaqs: japowFaqs,
    },
    europe: {
      country: "Europe",
      broad_category: "",
      region: "Europe",
      videos: {
        desktop: "https://d1puf2nnbcyl9q.cloudfront.net/1772454716354_website_eugoigo.mp4",
        mobile: "https://d1puf2nnbcyl9q.cloudfront.net/1772417727404_20260302-091328.mp4",
      },
      mainText: t("landing.destination.europe.main_text"),
      subText: t("landing.destination.europe.sub_text"),
      heroLogo: "/images/eu-go-i-go-logo-white.png",
      cta: {
        text: t("landing.destination.europe.cta_text"),
        scrollTarget: "destination-tours",
      },
      title: t("landing.destination.europe.title"),
      whyDestinationTitle: t("landing.destination.europe.why_destination_title"),
      whyDestinationDescription: t("landing.destination.europe.why_destination_description"),
      destinationReasons: europeDestinationReasons,
      customFaqs: europeFaqs,
    },
  };

  const config = destinationConfig[destinationKey.toLowerCase()];

  if (!config) {
    console.error(`Destination config not found for: ${destinationKey}`);
    return <div>{t("landing.destination.tours.destination_not_found")}</div>;
  }

  // eslint-disable-next-line no-unused-vars
  const handlePurchasePass = async () => {
    if (destinationKey === "beginner-leader-trips") {
      return;
    }

    let website = window.location.origin;

    const response = await customCheckout(
      "Weekend club pass",
      999,
      "sgd",
      `${website}/weekend?pass-purchased=true`,
      `${website}/weekend`,
      {
        paymentFor: "weekend_pass",
      }
    );

    const { url } = await response.data;

    window.location.href = url;
  };

  // Landing sections rendered between the hero and the tours list. Order is
  // per-destination via config.sectionOrder; defaults to the historical order
  // so every other destination page is unaffected.
  const sectionElements = {
    whyDestination: config.whyDestinationTitle ? (
      <WhyChooseUs
        mobileVariant={config.whyDestinationMobileScrollVariant || "horizontal"}
        title={config.whyDestinationTitle}
        description={config.whyDestinationDescription}
        reasons={config.destinationReasons}
        removeCta={true}
      />
    ) : null,
    whyUs: config.whyUsTitle ? (
      <WhyChooseUs
        mobileVariant={config.whyUsMobileScrollVariant || "vertical"}
        title={config.whyUsTitle}
        description={config.whyUsDescription}
        reasons={config.whyUsReasons}
        cta={() => {
          document.getElementById(config.cta.scrollTarget).scrollIntoView({
            behavior: "smooth",
          });
        }}
      />
    ) : null,
    reviews: config.reviewsTitle ? (
      <WhyChooseUs
        mobileVariant={config.reviewMobileScrollVariant || "horizontal"}
        title={config.reviewsTitle}
        description={config.reviewsDescription}
        reasons={config.reviewsReasons}
        removeCta={true}
        darkerPicture={true}
      />
    ) : null,
  };
  const sectionOrder = config.sectionOrder || ["whyDestination", "whyUs", "reviews"];

  return (
    <>
      <StickyHeader />
      {destinationKey === "beginner-leader-trips" && (
        <LeaderInvitationBanner invitationKey={invitationKey} />
      )}
      <HeroFeatured
        desktopVideo={config.videos.desktop}
        mobileVideo={config.videos.mobile}
        desktopImage={config?.image?.desktop ?? landingImageDesktop}
        mobileImage={config?.image?.mobile ?? landingImageMobile}
        mainText={config.mainText}
        subText={config.subText}
        heroLogo={config.heroLogo}
        ctaText={config.cta.text}
        onCtaClick={() => {
          document.getElementById(config.cta.scrollTarget).scrollIntoView({
            behavior: "smooth",
          });
        }}
      />

      {sectionOrder.map((sectionKey) => (
        <React.Fragment key={sectionKey}>{sectionElements[sectionKey]}</React.Fragment>
      ))}

      <Tours destinationKey={destinationKey} config={config} />
      <FAQs customFaqs={config.customFaqs} />
      <Footer />
    </>
  );
};

const Tours = ({ destinationKey, config }) => {
  const { t } = useTranslation();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const LIMIT = 6;

  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        setError(null);
        setSkip(0);

        let url = `/v1/project/getProjectsByCountry?`;

        if (config.region) {
          url += `region=${config.region}`;
        } else {
          url += `country=${config.country}`;
        }

        if (config.broad_category) {
          url += `&broad_category=${config.broad_category}`;
        }

        let data = await unauthAxiosFetcher(url);

        // Filter to only show upcoming trips for europe
        // Backend sets earliest_trip_start_date to "2999-12-31" when no upcoming trips exist
        if (config.region === "Europe" && data.allProjects) {
          data = {
            ...data,
            allProjects: data.allProjects.filter(
              (project) =>
                project.earliest_trip_start_date &&
                project.earliest_trip_start_date !== "2999-12-31"
            ),
          };
        }

        setTours(data);

        if (destinationKey === "beginner-leader-trips") {
          setHasMore(data.allProjects && data.allProjects.length >= LIMIT);
        }
      } catch (err) {
        console.error(`Error fetching tours for ${config.country}:`, err);
        setError(t("landing.destination.tours.error", { country: config.country }));
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationKey, config.country, config.broad_category]);

  // eslint-disable-next-line no-unused-vars
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const newSkip = skip + LIMIT;

      const data = await unauthAxiosFetcher(
        `/v1/project/getProjectsByCountry?country=${config.country}&broad_category=${config.broad_category}&leader_requirements=Junior duck&skip=${newSkip}&limit=${LIMIT}`
      );

      setTours((prevTours) => ({
        ...prevTours,
        allProjects: [...prevTours.allProjects, ...data.allProjects],
      }));

      setSkip(newSkip);
      // If we got fewer items than the limit, there are no more items
      setHasMore(data.allProjects && data.allProjects.length >= LIMIT);
    } catch (err) {
      console.error("Error loading more tours:", err);
      setError(t("landing.destination.tours.error", { country: config.country }));
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-px-4 tw-py-4 md:tw-px-16 md:tw-py-16" id="destination-tours">
        <div className="tw-mb-4 tw-text-[25px] tw-font-extrabold md:tw-text-[25px]">
          {t("landing.destination.tours.loading", { title: config.title })}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-px-4 tw-py-4 md:tw-px-16 md:tw-py-16" id="destination-tours">
        <div className="tw-mb-4 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
          {config.title}
        </div>
        <div className="tw-text-red-500">{error}</div>
      </div>
    );
  }

  if (destinationKey === "weekend") {
    return (
      <div className="tw-px-4 tw-py-4 md:tw-px-16 md:tw-py-16" id="destination-tours">
        <div className="tw-mb-7 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
          {config.title}
          {/* <p style={{ fontSize: "16px", fontWeight: 700 }}>
            Weekend pass holders can enjoy any 4 trips below for just $999
          </p> */}
        </div>

        <DisplayRow
          rowTitle={t("landing.destination.weekend.action_packed_day_trips")}
          trips={tours.dayTripProjects}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.weekend.zero_leaves_required")}
          trips={tours.noLeaveProjects}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.weekend.the_break_you_deserve")}
          trips={tours.threeToFiveDaysProjects}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />
      </div>
    );
  }

  if (destinationKey === "world-wonders") {
    return (
      <div className="tw-px-4 tw-py-4 md:tw-px-16 md:tw-py-16" id="destination-tours">
        <div className="tw-mb-7 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
          {config.title}
          {/* <p style={{ fontSize: "16px", fontWeight: 700 }}>
            Weekend pass holders can enjoy any 4 trips below for just $999
          </p> */}
        </div>

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.last_frontier_header")}
          rowDesc={t("landing.destination.world-wonders.last_frontier_description")}
          trips={tours.worldWonder.last_frontier}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.aurora_header")}
          rowDesc={t("landing.destination.world-wonders.aurora_description")}
          trips={tours.worldWonder.aurora}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.world_festivals_header")}
          rowDesc={t("landing.destination.world-wonders.world_festivals_description")}
          trips={tours.worldWonder.festivals}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.sea_wayfarers_header")}
          rowDesc={t("landing.destination.world-wonders.sea_wayfarers_description")}
          trips={tours.worldWonder.ship}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.safari_header")}
          rowDesc={t("landing.destination.world-wonders.safari_description")}
          trips={tours.worldWonder.safari}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.ancient_header")}
          rowDesc={t("landing.destination.world-wonders.ancient_description")}
          trips={tours.worldWonder.ancient}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />

        <DisplayRow
          rowTitle={t("landing.destination.world-wonders.gatekept_header")}
          rowDesc={t("landing.destination.world-wonders.gatekept_description")}
          trips={tours.worldWonder.gatekeep}
          projectView={true}
          rowStyle={{
            margin: 0,
          }}
          rowTitleStyle={{
            fontSize: "19px",
            fontWeight: 800,
          }}
        />
      </div>
    );
  }

  return (
    <div className="tw-px-4 tw-py-4 md:tw-px-16 md:tw-py-16" id="destination-tours">
      {tours.allProjects.length === 0 ? (
        <div className="tw-text-gray-500">
          {t("landing.destination.tours.no_tours_available", { country: config.country })}
        </div>
      ) : (
        <>
          <DisplayRow
            rowTitle={config.title}
            trips={tours.allProjects}
            projectView={true}
            rowStyle={{
              margin: 0,
            }}
            rowTitleStyle={{
              fontSize: "25px",
              fontWeight: 800,
            }}
          />
        </>
      )}
    </div>
  );
};

export default Destination;
